import { type Timezone_userProfile$key } from '#app/__generated__/relay/Timezone_userProfile.graphql.js';
import { InlineEditContainer } from '#app/components/InlineEditContainer/InlineEditContainer.js';
import {
  TIMEZONE_OFFSETS,
  TIMEZONES,
} from '#app/components/TimezoneInput/constants.js';
import {
  type TimezoneOffset,
  type Timezone as TimezoneType,
} from '#app/components/TimezoneInput/types.js';
import { InlineEditingEmptyPlaceholder } from '../InlineEditingEmptyPlaceholder.js';
import { ClockIcon, EditIcon } from '@contra/icons/icons';
import { graphql, useFragment } from '@contra/relay';
import { lazy, Suspense, useState } from 'react';
import { styled } from 'styled-components';

const LazyTimezoneInlineEditing = lazy(async () => {
  return {
    default: (await import('./TimezoneInlineEditing.js')).TimezoneInlineEditing,
  };
});

const TimezoneLayout = styled.div`
  display: inline-flex;
  align-items: center;
  gap: 12px;

  > svg {
    flex-shrink: 0;
  }
`;

// (GMT-8) Pacific Standard Time (PST), ± 3 hours
const getTimezoneString = ({
  offsetTolerance,
  primaryUtcOffset,
}: {
  offsetTolerance: TimezoneOffset;
  primaryUtcOffset: TimezoneType;
}) => {
  return `${
    primaryUtcOffset.label
  }, ± ${offsetTolerance.label.toLocaleLowerCase()}`;
};

export const Timezone = ({
  userProfileRef,
}: {
  readonly userProfileRef: Timezone_userProfile$key;
}) => {
  const data = useFragment(
    graphql`
      fragment Timezone_userProfile on UserProfile {
        workPreferences {
          timezonePrimaryUtcOffset
          timezoneOffsetTolerance
        }
        visitorCanEdit
      }
    `,
    userProfileRef,
  );

  const { visitorCanEdit, workPreferences } = data;

  const [isInlineEditing, setIsInlineEditing] = useState(false);

  const [primaryUtcOffset, setPrimaryUtcOffset] = useState<
    TimezoneType | undefined
  >(
    TIMEZONES.find(
      (zone) => zone.offset === workPreferences.timezonePrimaryUtcOffset,
    ),
  );

  const [offsetTolerance, setOffsetTolerance] = useState<
    TimezoneOffset | undefined
  >(
    TIMEZONE_OFFSETS.find(
      (tzOffset) => tzOffset.offset === workPreferences.timezoneOffsetTolerance,
    ),
  );

  const timezoneIcon = <ClockIcon size={16} />;

  const visitorCanEditDefaultState =
    primaryUtcOffset && offsetTolerance ? (
      <InlineEditContainer
        label="Edit your timezone"
        onClick={() => setIsInlineEditing(true)}
        visitorCanEdit={visitorCanEdit}
      >
        <TimezoneLayout>
          {timezoneIcon}
          {getTimezoneString({ offsetTolerance, primaryUtcOffset })}
        </TimezoneLayout>
        <EditIcon size={16} />
      </InlineEditContainer>
    ) : (
      <InlineEditingEmptyPlaceholder
        label="Add timezone"
        onClick={() => {
          setIsInlineEditing(true);
        }}
      />
    );

  if (visitorCanEdit) {
    if (isInlineEditing) {
      return (
        <Suspense fallback={visitorCanEditDefaultState}>
          <LazyTimezoneInlineEditing
            offsetTolerance={offsetTolerance}
            primaryUtcOffset={primaryUtcOffset}
            setIsInlineEditing={setIsInlineEditing}
            setOffsetTolerance={setOffsetTolerance}
            setPrimaryUtcOffset={setPrimaryUtcOffset}
          />
        </Suspense>
      );
    }

    return visitorCanEditDefaultState;
  }

  if (primaryUtcOffset && offsetTolerance) {
    return (
      <TimezoneLayout>
        {timezoneIcon}
        {getTimezoneString({ offsetTolerance, primaryUtcOffset })}
      </TimezoneLayout>
    );
  }

  return null;
};
